import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';

import {
  CalendarCommonModule,
  CalendarDayModule,
  CalendarEvent,
  CalendarMonthModule,
  CalendarView,
  CalendarWeekModule,
} from 'angular-calendar';

import {
  NgbModal,
  NgbPopoverModule,
} from '@ng-bootstrap/ng-bootstrap';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import {
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import { Subject } from 'rxjs';

import {
  ScheduleDetailsModalComponent,
} from '../schedule-details-modal/schedule-details-modal.component';

import {
  ScheduleRescheduleModalComponent,
  ScheduleRescheduleModalResult,
} from '../schedule-reschedule-modal/schedule-reschedule-modal.component';

import {
  ConfirmationModalComponent,
} from '@main/confirmation-modal/confirmation-modal.component';

import {
  getYearsRange,
  MONTHS,
} from '@app-shared/constants/constants';

import {
  isDateOnly,
  parseScheduleDate,
} from '@main/utils/date-time.util';

export type ScheduleCalendarRow = {
  scheduleId: string;
  source: string;
  projectName: string;
  itemCategory: string;
  clientName: string;
  itemId: string;
  status: string;
  scheduledFor: string;
};

export type ScheduleCalendarMeta = {
  scheduleItem: ScheduleCalendarRow;
};

export type ScheduleCalendarDateChange = {
  month: number;
  year: number;
};

@Component({
  selector: 'app-schedule-calendar-jobs',
  standalone: true,
  imports: [
    CommonModule,
    CalendarCommonModule,
    CalendarMonthModule,
    CalendarWeekModule,
    CalendarDayModule,
    FontAwesomeModule,
    NgbPopoverModule,
  ],
  templateUrl: './schedule-calendar-jobs.component.html',
  styleUrls: ['./schedule-calendar-jobs.component.css'],
})
export class ScheduleCalendarJobsComponent {
  private readonly modalService = inject(NgbModal);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly dataSignal =
    signal<ScheduleCalendarRow[]>([]);

  @Input()
  set data(
    value: ScheduleCalendarRow[] | null | undefined,
  ) {
    this.dataSignal.set(value ?? []);
    this.buildEvents();
  }
  @Output()
  dateChange =
    new EventEmitter<ScheduleCalendarDateChange>();

  @Output()
  reschedule =
    new EventEmitter<ScheduleRescheduleModalResult>();

  @Output()
  cancel =
    new EventEmitter<ScheduleCalendarRow>();

  readonly CalendarView = CalendarView;

  readonly faCalendarDays = faCalendarDays;
  readonly faChevronLeft = faChevronLeft;
  readonly faChevronRight = faChevronRight;

  view: CalendarView = CalendarView.Month;

  viewDate = new Date();

  activeDayIsOpen = false;

  rowForCancellation: ScheduleCalendarRow | null = null;

  refresh = new Subject<void>();

  events: CalendarEvent<ScheduleCalendarMeta>[] = [];

  /**
   * Switch Month / Week / Day view.
   */
  setView(view: CalendarView): void {
    this.view = view;
    this.activeDayIsOpen = false;

    this.refresh.next();
    this.cdr.markForCheck();
  }

  /**
   * Builds angular-calendar events
   * whenever new API data comes from the parent.
   */
  private buildEvents(): void {
    const rows = this.dataSignal();

    this.events = rows
      .filter((item) => {
        if (!item.scheduledFor) {
          return false;
        }

        const scheduledDate = parseScheduleDate(
          item.scheduledFor,
        );

        return !Number.isNaN(
          scheduledDate.getTime(),
        );
      })
      .map((item) => {
        const scheduledDate = parseScheduleDate(
          item.scheduledFor,
        );

        return {
          title:
            `${item.projectName} - ${item.itemCategory}`,

          start: scheduledDate,

          allDay: isDateOnly(
            item.scheduledFor,
          ),

          meta: {
            scheduleItem: item,
          },
        };
      });

    this.refresh.next();
    this.cdr.markForCheck();
  }

  /**
   * Previous calendar period.
   */
  previousPeriod(): void {
    const next = new Date(this.viewDate);

    if (this.view === CalendarView.Month) {
      next.setMonth(
        this.viewDate.getMonth() - 1,
      );
    }

    if (this.view === CalendarView.Week) {
      next.setDate(
        this.viewDate.getDate() - 7,
      );
    }

    if (this.view === CalendarView.Day) {
      next.setDate(
        this.viewDate.getDate() - 1,
      );
    }

    this.viewDate = next;

    this.viewDateChanged();
  }

  /**
   * Next calendar period.
   */
  nextPeriod(): void {
    const next = new Date(this.viewDate);

    if (this.view === CalendarView.Month) {
      next.setMonth(
        this.viewDate.getMonth() + 1,
      );
    }

    if (this.view === CalendarView.Week) {
      next.setDate(
        this.viewDate.getDate() + 7,
      );
    }

    if (this.view === CalendarView.Day) {
      next.setDate(
        this.viewDate.getDate() + 1,
      );
    }

    this.viewDate = next;

    this.viewDateChanged();
  }

  /**
   * Return calendar to today.
   */
  today(): void {
    this.viewDate = new Date();

    this.viewDateChanged();
  }

  /**
   * MONTHS constant is assumed to use:
   *
   * January = 1
   * February = 2
   * ...
   * December = 12
   */
  get currentMonth(): number {
    return this.viewDate.getMonth();
  }

  get currentYear(): number {
    return this.viewDate.getFullYear();
  }

  get months(): typeof MONTHS {
    return MONTHS;
  }

  get years(): number[] {
    return getYearsRange(this.currentYear);
  }

  /**
   * Month dropdown.
   *
   * Incoming month is 1-12.
   * Date.setMonth() expects 0-11.
   */
  onMonthChange(month: number): void {
    const newDate = new Date(this.viewDate);

    newDate.setMonth(month);

    this.viewDate = newDate;

    this.viewDateChanged();
  }

  /**
   * Year dropdown.
   */
  onYearChange(year: number): void {
    const newDate = new Date(this.viewDate);

    newDate.setFullYear(year);

    this.viewDate = newDate;

    this.viewDateChanged();
  }

  /**
   * Called after Previous / Next / Today /
   * Month change / Year change.
   */
  private viewDateChanged(): void {
    this.activeDayIsOpen = false;

    this.emitDateChange();

    this.refresh.next();
    this.cdr.markForCheck();
  }

  /**
   * Tell parent to reload the API using
   * the currently selected month/year.
   */
  private emitDateChange(): void {
    this.dateChange.emit({
      month: this.viewDate.getMonth() + 1,
      year: this.viewDate.getFullYear(),
    });
  }

  /**
   * Open schedule details.
   */
  openDetails(
    row: ScheduleCalendarRow,
  ): void {
    const modalRef = this.modalService.open(
      ScheduleDetailsModalComponent,
      {
        size: 'md',
        centered: true,
        scrollable: true,
        windowClass: 'schedule-details-modal',
      },
    );

    modalRef.componentInstance.row = row;

    modalRef.result
      .then((result) => {
        if (!result) {
          return;
        }

        if (
          result.action === 'reschedule' &&
          result.row
        ) {
          this.openRescheduleModal(
            result.row,
          );

          return;
        }

        if (
          result.action === 'cancel' &&
          result.row
        ) {
          this.openCancelScheduleConfirmationModal(
            result.row,
          );
        }
      })
      .catch(() => {
        // Details modal dismissed
      });
  }

  /**
   * Event background color based on status.
   */
  setEventBgColor(
    scheduleItem: ScheduleCalendarRow,
  ): Record<string, string> {
    const status = this.normalizeStatus(
      scheduleItem.status,
    );

    if (
      status === 'cancelled' ||
      status === 'canceled'
    ) {
      return {
        'background-color': '#dc3545',
        color: '#ffffff',
      };
    }

    if (status === 'failed') {
      return {
        'background-color': '#b42318',
        color: '#ffffff',
      };
    }

    if (status === 'done') {
      return {
        'background-color': '#43885a',
        color: '#ffffff',
      };
    }

    if (status === 'scheduled') {
      return {
        'background-color': '#9a6b00',
        color: '#ffffff',
      };
    }

    return {
      'background-color': '#245b20',
      color: '#ffffff',
    };
  }

  formatDateTime(
    value?: string,
  ): string {
    if (!value) {
      return '-';
    }

    const date = parseScheduleDate(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }

  formatTime(
    value?: string,
  ): string {
    if (!value) {
      return '-';
    }

    const date = parseScheduleDate(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Reschedule modal.
   */
  openRescheduleModal(
    row: ScheduleCalendarRow,
  ): void {
    if (!row) {
      return;
    }

    const modalRef = this.modalService.open(
      ScheduleRescheduleModalComponent,
      {
        size: 'md',
        centered: true,
        backdrop: 'static',
        windowClass:
          'schedule-reschedule-modal',
      },
    );

    modalRef.componentInstance.row = row;

    modalRef.result
      .then(
        (
          result: ScheduleRescheduleModalResult,
        ) => {
          if (!result) {
            return;
          }

          this.reschedule.emit(result);
        },
      )
      .catch(() => {
        // Modal dismissed
      });
  }

  /**
   * Cancel confirmation.
   */
  openCancelScheduleConfirmationModal(
    row: ScheduleCalendarRow,
  ): void {
    this.rowForCancellation = row;

    const confirmationModalRef =
      this.modalService.open(
        ConfirmationModalComponent,
      );

    confirmationModalRef.componentInstance.message =
      'Are you sure you would like to cancel this schedule?';

    confirmationModalRef.componentInstance.actionBtnTitle =
      'Confirm';

    confirmationModalRef.componentInstance.confirmed.subscribe(
      () => {
        this.cancel.emit(row);

        this.rowForCancellation = null;
      },
    );
  }

  private normalizeStatus(
    status: string,
  ): string {
    return (status ?? '')
      .trim()
      .toLowerCase();
  }
}